// PR-F — staff ACH mandate capture card on the loan Overview tab. Staff set
// up the buyer's bank-debit mandate once (a Stripe us_bank_account SetupIntent
// on the loan's TPS connected account); the buyer can then pay bills
// off-session with one button. Mounted gated Loans.Manage.
//
// Reuses the InvoicePayPanel Stripe-in-app mechanics (loadStripe cached per
// publishable-key + connected-account, <Elements>/<PaymentElement>, confirm
// with redirect: "if_required"), but here it confirms a SetupIntent — there's
// no charge. The confirmed bank state is stamped by the webhook, so after a
// successful confirm we re-fetch the GET status (a manual Refresh covers the
// brief webhook lag).

import { useEffect, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Button, Card, SubmitButton } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import {
  achMandateApi,
  type AchMandateSetupIntent,
  type AchMandateStatus,
} from "@/api/portfolioAdmin/achMandate";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

// One Stripe.js load per (publishable key + connected account) for the whole
// session — the connected account differs per loan, so it's part of the key
// (loadStripe must not be called per render).
const stripeLoaders = new Map<string, Promise<Stripe | null>>();
function stripeFor(publishableKey: string, stripeAccount: string): Promise<Stripe | null> {
  const cacheKey = `${publishableKey}:${stripeAccount}`;
  let loader = stripeLoaders.get(cacheKey);
  if (!loader) {
    loader = loadStripe(publishableKey, { stripeAccount });
    stripeLoaders.set(cacheKey, loader);
  }
  return loader;
}

export function AchMandateCard({ loan }: { loan: LoanSummaryDto }) {
  const { tz } = useDateFormatter();
  const [status, setStatus] = useState<AchMandateStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [intent, setIntent] = useState<AchMandateSetupIntent | null>(null);
  const [beginError, setBeginError] = useState<string | null>(null);
  const [beginning, setBeginning] = useState(false);

  const refreshStatus = async () => {
    const data = await achMandateApi.status(loan.id);
    setStatus(data);
    return data;
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await achMandateApi.status(loan.id);
        if (!cancelled) setStatus(data);
      } catch (err) {
        if (!cancelled) setLoadError(extractErrorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loan.id]);

  const begin = async () => {
    setBeginError(null);
    setBeginning(true);
    try {
      const data = await achMandateApi.begin(loan.id);
      setIntent(data);
    } catch (err) {
      setBeginError(extractErrorMessage(err));
    } finally {
      setBeginning(false);
    }
  };

  return (
    <Card data-testid="ach-mandate-card">
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-1">
        Bank payment (ACH)
      </h3>

      {loadError && (
        <p className="text-small text-status-error m-0" role="alert">
          {loadError}
        </p>
      )}

      {!loadError && !status && (
        <p className="text-small text-gray-mid m-0">Loading…</p>
      )}

      {status?.mandateOnFile && (
        <p className="text-small text-onyx m-0" data-testid="ach-mandate-on-file">
          Bank on file
          {status.bankLast4 && <> (ending {status.bankLast4})</>}
          {status.confirmedAt && (
            <span className="text-gray-mid">
              {" "}
              — confirmed {formatDateInTz(status.confirmedAt, tz)}
            </span>
          )}
        </p>
      )}

      {status && !status.mandateOnFile && !intent && (
        <div className="flex flex-col items-start gap-space-2">
          <p className="text-small text-gray-mid m-0">
            No bank mandate on file — set one up so the buyer can pay bills
            online.
          </p>
          {beginError && (
            <p className="text-small text-status-error m-0" role="alert">
              {beginError}
            </p>
          )}
          <Button
            size="sm"
            variant="primary"
            loading={beginning}
            onClick={() => void begin()}
            data-testid="ach-mandate-setup"
          >
            Set up bank payment
          </Button>
        </div>
      )}

      {intent && (
        <Elements
          stripe={stripeFor(intent.publishableKey, intent.stripeAccount)}
          options={{ clientSecret: intent.clientSecret }}
        >
          <ConfirmMandateForm
            onConfirmed={refreshStatus}
            onCancel={() => setIntent(null)}
          />
        </Elements>
      )}
    </Card>
  );
}

function ConfirmMandateForm({
  onConfirmed,
  onCancel,
}: {
  onConfirmed: () => Promise<AchMandateStatus>;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [savedPendingConfirm, setSavedPendingConfirm] = useState(false);

  const confirm = async () => {
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    try {
      // The SetupIntent's payment_method_types is us_bank_account, so the
      // Payment Element only offers the bank-account flow (no card tab).
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (result.error) {
        setError(
          result.error.message ??
            "The bank account could not be saved. Please try again.",
        );
        return;
      }
      // Mandate captured — the webhook stamps the confirmed status; reflect it
      // optimistically and re-fetch (a manual Refresh covers any lag).
      setSavedPendingConfirm(true);
      await onConfirmed();
      toast.success("Bank saved — confirming…");
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-space-3"
      onSubmit={(e) => {
        e.preventDefault();
        void confirm();
      }}
    >
      <PaymentElement />
      <p className="text-caption text-gray-mid m-0">
        The buyer authorizes a bank-debit (ACH) mandate. No charge is made now;
        future bill payments settle in a few business days.
      </p>
      {savedPendingConfirm && (
        <p className="text-small text-onyx m-0" data-testid="ach-mandate-saved">
          Bank saved — confirming…
        </p>
      )}
      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-space-2">
        <SubmitButton
          type="submit"
          variant="primary"
          size="sm"
          pending={submitting}
          pendingLabel="Saving…"
          disabled={!stripe || !elements}
          data-testid="ach-mandate-confirm"
        >
          Save bank account
        </SubmitButton>
        <Button
          type="button"
          variant="text"
          size="sm"
          onClick={() => void onConfirmed()}
          disabled={submitting}
          data-testid="ach-mandate-refresh"
        >
          Refresh
        </Button>
        <Button
          type="button"
          variant="text"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
